// The Library: Foundation's catalog, read from the installed package. It is
// Foundation's own documentation (no client data), so it ships in the bundle
// and needs no API. Kept free of React so it can be unit tested.

import {
  catalog,
  type Catalog,
  type CatalogEntry,
  type CatalogStatus,
  type DomainId,
  type ShelfId,
} from '@olytic/foundation/catalog';

import { LIBRARY_ROUTE } from '../navigation';

export { LIBRARY_ROUTE };

export type LibraryTab = 'browse' | 'tasks' | 'glossary';

/** The view that shows every shelf at once. */
export const ALL_SHELVES = 'all';

export function entryHref(entry: Pick<CatalogEntry, 'id'>): string {
  return `${LIBRARY_ROUTE}/${encodeURIComponent(entry.id)}`;
}

/** The Library's Browse tab, on one shelf (or every shelf) and optionally one area of the business. */
export function browseHref({ shelf, domain }: { shelf?: ShelfId | typeof ALL_SHELVES; domain?: DomainId } = {}): string {
  const params = new URLSearchParams();
  if (shelf) params.set('shelf', shelf);
  if (domain) params.set('area', domain);
  const query = params.toString();
  return query ? `${LIBRARY_ROUTE}?${query}` : LIBRARY_ROUTE;
}

export function libraryTabHref(tab: LibraryTab): string {
  return tab === 'browse' ? LIBRARY_ROUTE : `${LIBRARY_ROUTE}?tab=${tab}`;
}

/** Where the showcase (the live demos) is served, behind the same sign-in (apps/server/src/static.mjs). */
export function showcaseHref(entry: Pick<CatalogEntry, 'showcase'>): string | null {
  return entry.showcase ? `/library/#${entry.showcase}` : null;
}

/** One building block, as the singular of its shelf ("Blueprint", "Pattern"). */
const SHELF_SINGULAR: Record<ShelfId, string> = {
  blueprints: 'Blueprint',
  kits: 'Domain kit part',
  patterns: 'Pattern',
  components: 'Component',
  foundations: 'Foundation',
};

export function shelfSingular(shelf: ShelfId): string {
  return SHELF_SINGULAR[shelf];
}

export const STATUS_LABEL: Record<CatalogStatus, string> = { stable: 'Ready to use', new: 'New', retired: 'Retired' };
export const STATUS_TONE: Record<CatalogStatus, 'success' | 'info' | 'neutral'> = { stable: 'success', new: 'info', retired: 'neutral' };

/**
 * The catalog's lookups. Built per catalog so a test can hand in its own; the
 * app uses the installed package's.
 */
export function libraryOf(source: Catalog) {
  const byId = new Map(source.entries.map((e) => [e.id, e]));
  const shelfById = new Map(source.shelves.map((s) => [s.id, s]));
  const domainById = new Map(source.domains.map((d) => [d.id, d]));
  const termById = new Map(source.glossary.map((t) => [t.id, t]));
  const shelfOrder = new Map([...source.shelves].sort((a, b) => a.code - b.code).map((s, i) => [s.id, i]));

  /** An entry by its id ("record-page") or code ("104"). */
  function findEntry(ref: string): CatalogEntry | undefined {
    return byId.get(ref) ?? (/^\d+$/.test(ref) ? source.entries.find((e) => e.code === Number(ref)) : undefined);
  }

  /** Entries for ids, in the order given, skipping any the catalog no longer has. */
  function entriesFor(ids: readonly string[]): CatalogEntry[] {
    return ids.flatMap((id) => byId.get(id) ?? []);
  }

  /** Shelves first to last (Blueprints, then Domain kits …), then by code. */
  function compareEntries(a: CatalogEntry, b: CatalogEntry): number {
    return (shelfOrder.get(a.shelf) ?? 0) - (shelfOrder.get(b.shelf) ?? 0) || a.code - b.code;
  }

  return {
    catalog: source,
    shelves: [...source.shelves].sort((a, b) => a.code - b.code),
    domains: source.domains,
    entries: [...source.entries].sort(compareEntries),
    findEntry,
    entriesFor,
    shelfOf: (id: ShelfId) => shelfById.get(id),
    domainOf: (id: DomainId) => domainById.get(id),
    termOf: (id: string) => termById.get(id),
    /** The glossary and the "I want to…" index arrive with a later Foundation; until then the tabs stay hidden. */
    hasGlossary: source.glossary.length > 0,
    hasTasks: source.tasks.length > 0,
  };
}

export type Library = ReturnType<typeof libraryOf>;

export const library: Library = libraryOf(catalog);

/** The words an entry is found by: its name, other names, purpose, kit and code. */
function searchText(entry: CatalogEntry): string {
  return [entry.name, ...entry.aliases, entry.purpose, entry.kit ?? '', String(entry.code), entry.id.replace(/-/g, ' ')]
    .join(' ')
    .toLowerCase();
}

/** Whether the entry contains every word of the query. An empty query matches everything. */
export function matchesQuery(entry: CatalogEntry, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const text = searchText(entry);
  return words.every((w) => text.includes(w));
}

/** The entries to list: retired ones only when asked for by shelf view "all". */
export function browseEntries(
  lib: Library,
  { shelf, domain, query }: { shelf: string; domain?: string | undefined; query: string },
): CatalogEntry[] {
  return lib.entries.filter(
    (e) =>
      (shelf === ALL_SHELVES || e.shelf === shelf) &&
      (!domain || e.domains.includes(domain as DomainId)) &&
      matchesQuery(e, query),
  );
}

/** "100 to 199": the codes a shelf's entries carry. */
export function shelfRange(code: number): string {
  return `${code} to ${code + 99}`;
}
